import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'poettik_region';

/**
 * Hardcoded indicative FX rate. Stripe charges in GBP — the USD shown on
 * pricing is a "you'll pay roughly this much" estimate for non-UK shoppers.
 * Bump this once a quarter (or pull from frankfurter.app if it ever matters).
 */
export const GBP_TO_USD = 1.27;

/** Auto-detect region from browser locale + timezone. */
const detectRegion = () => {
  if (typeof window === 'undefined') return 'uk';
  try {
    const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (lang.startsWith('en-us') || lang === 'en' && (navigator.languages || []).some(l => l.toLowerCase().startsWith('en-us'))) {
      return 'us';
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    // Any America/* timezone is treated as US for tone & currency purposes.
    if (tz.startsWith('America/')) return 'us';
  } catch (e) {
    /* ignore – fall through to default */
  }
  return 'uk';
};

const RegionContext = createContext({
  region: 'uk',
  currency: 'GBP',
  isAutoDetected: true,
  setRegion: () => {},
  toggleCurrency: () => {},
  formatPrice: (gbp) => `£${Number(gbp).toFixed(2)}`,
  formatDate: (iso) => '',
});

export const RegionProvider = ({ children }) => {
  const { user, getAuthHeader } = useAuth();
  // Boot value: user-saved choice > auto-detected from browser.
  const [region, setRegionState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'uk' || saved === 'us') return saved;
    } catch (e) { /* noop */ }
    return detectRegion();
  });
  const [isAutoDetected, setIsAutoDetected] = useState(() => {
    try { return !localStorage.getItem(STORAGE_KEY); } catch (e) { return true; }
  });

  // Once a user signs in and has a saved profile region, prefer that.
  useEffect(() => {
    if (user?.region === 'uk' || user?.region === 'us') {
      setRegionState(user.region);
      setIsAutoDetected(false);
      try { localStorage.setItem(STORAGE_KEY, user.region); } catch (e) { /* noop */ }
    }
  }, [user?.region]);

  const setRegion = useCallback(async (next, { persistToServer = true } = {}) => {
    if (next !== 'uk' && next !== 'us') return;
    setRegionState(next);
    setIsAutoDetected(false);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* noop */ }
    // If signed in, keep the profile in sync so AI generations honour it.
    if (persistToServer && user) {
      try {
        await axios.put(`${API}/api/auth/profile`, { region: next }, { headers: getAuthHeader?.() || {} });
      } catch (err) {
        /* non-fatal: local toggle still works */
      }
    }
  }, [user, getAuthHeader]);

  const toggleCurrency = useCallback(() => {
    setRegion(region === 'uk' ? 'us' : 'uk');
  }, [region, setRegion]);

  const value = useMemo(() => {
    const currency = region === 'us' ? 'USD' : 'GBP';
    const symbol = currency === 'USD' ? '$' : '£';

    /**
     * Format a price for display.
     *  - Pass a number (GBP) → converted to USD at indicative FX when region is US.
     *  - Pass an object `{gbp, usd}` → uses the explicit amount for the active
     *    region. Lets us show round US prices ($11.99) that aren't a straight
     *    FX conversion from GBP.
     */
    const formatPrice = (amount, { showSymbol = true } = {}) => {
      let n;
      if (amount && typeof amount === 'object') {
        const explicit = currency === 'USD' ? amount.usd : amount.gbp;
        n = Number(explicit);
      } else {
        const raw = Number(amount);
        if (!Number.isFinite(raw)) return '';
        n = currency === 'USD' ? raw * GBP_TO_USD : raw;
      }
      if (!Number.isFinite(n)) return '';
      const str = n.toFixed(2);
      return showSymbol ? `${symbol}${str}` : str;
    };
    const formatDate = (iso, opts = {}) => {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleDateString(region === 'us' ? 'en-US' : 'en-GB', opts);
      } catch (e) {
        return '';
      }
    };
    return { region, currency, isAutoDetected, setRegion, toggleCurrency, formatPrice, formatDate };
  }, [region, isAutoDetected, setRegion, toggleCurrency]);

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = () => useContext(RegionContext);
